import { Box, BoxProps, useBoolean } from "@chakra-ui/react";
import { useContextPreviewBlackboard } from "components/containers/inspectionDetail/context/PreviewBlackboard";
import { PinDetail } from "interfaces/models/pin";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { ImageHasBlackboardValue } from "..";
import {
  checkIsInitDefaultBlackboardPosition,
  handleInitDefaultBlackboardPosition,
  handleScaleBlackboardPosition,
} from "../../../../helpers/blackboard";
import Blackboard from "../Blackboard";
import Image from "components/Image";

export interface ImageWithBlackboardProps extends ImageHasBlackboardValue {
  pinDetail: PinDetail | undefined | null;
  boxProps?: BoxProps;
  defaultFormValue?: Record<string, any>;
  isModeEditor?: boolean;
}

const ImageWithBlackboard = (props: ImageWithBlackboardProps) => {
  const {
    src,
    pinDetail,
    blackboardData,
    boxProps,
    defaultFormValue,
    isModeEditor,
  } = props;

  const [isLoadedImage, loadedImage] = useBoolean();
  const [size, setSize] = useState<
    { width: number; height: number } | undefined
  >();
  const [position, setPosition] = useState<
    { x: number; y: number } | undefined
  >();
  const [imageEle, setImageEle] = useState<HTMLImageElement | undefined>();

  const { blackboardFormat, blackboardFrames } = useContextPreviewBlackboard();

  const calculateScale = useCallback(
    (imageEle: HTMLImageElement) => {
      const { clientHeight, clientWidth } = imageEle;

      if (!clientWidth || !clientHeight || !blackboardFormat) {
        return;
      }

      let result = {} as any;
      const blackboardPosition = blackboardData;

      const isInitDefaultPosition = checkIsInitDefaultBlackboardPosition({
        blackboardFormatWidth: blackboardFormat.width,
        blackboardFormatHeight: blackboardFormat.height,
        blackboardPosition,
      });

      if (!blackboardPosition || isInitDefaultPosition) {
        result = handleInitDefaultBlackboardPosition({
          boundWidth: clientWidth,
          boundHeight: clientHeight,
          blackboardFormatWidth: blackboardFormat?.width,
          blackboardFormatHeight: blackboardFormat?.height,
        });
      } else {
        result = handleScaleBlackboardPosition({
          boundWidth: clientWidth,
          boundHeight: clientHeight,
          blackboardPosition,
        });
      }

      setSize(result?.size);
      setPosition(result?.position);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src, blackboardFormat?.width, blackboardFormat?.height, blackboardData]
  );

  const onLoadedImage = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const target = e.target as HTMLImageElement;
      setImageEle(target);

      loadedImage.on();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!imageEle) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      calculateScale(imageEle);
    });

    resizeObserver.observe(imageEle);

    return () => {
      resizeObserver.unobserve(imageEle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageEle, calculateScale]);

  const scale = useMemo(() => {
    if (!blackboardFormat?.width || !size?.width) {
      return 1;
    }

    return size?.width / blackboardFormat.width;
  }, [blackboardFormat?.width, size?.width]);

  if (!src) {
    return <></>;
  }

  return (
    <Box
      {...boxProps}
      position="relative"
      height="100%"
      width="fit-content"
      maxHeight="fit-content"
    >
      {(!isLoadedImage || !size || !position) &&
        blackboardFormat?.layoutUrl && <Box className="chakra-spinner" />}
      <Image
        src={src}
        onLoad={onLoadedImage}
        width="auto"
        height="auto"
        objectFit="contain"
        maxHeight="100%"
        isSelfContainer={true}
      />
      {isLoadedImage && size && position && (
        <Blackboard
          position="absolute"
          top={`${position?.y}px`}
          left={`${position?.x}px`}
          width={`${blackboardFormat?.width}px`}
          height={`${blackboardFormat?.height}px`}
          transform={`translate(-${
            ((blackboardFormat?.width || 1) / 2) * (1 - scale)
          }px, -${
            ((blackboardFormat?.height || 1) / 2) * (1 - scale)
          }px) scale(${scale})`}
          blackBoardFramesProps={{
            zoomRatio: 1,
          }}
          layoutUrl={blackboardFormat?.layoutUrl}
          pinDetail={pinDetail}
          blackboardFrames={blackboardFrames}
          blackboardWidth={size?.width}
          blackboardHeight={size?.height}
          blackboardData={blackboardData}
          defaultFormValue={defaultFormValue}
          isModeEditor={isModeEditor}
        />
      )}
    </Box>
  );
};

export default memo(ImageWithBlackboard);
